import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
  Info as InfoIcon,
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import XLSX from "xlsx-js-style";
import moment from "moment";
import toast from "react-hot-toast";
import "moment/locale/pt";

import { useLoading } from "../../../../../shared/context/LoadingContext";
import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import ClientesAutocomplete from "../../../../../shared/components/clientesAutocomplete/ClientesAutocomplete";
import MultiSelectRelatorio from "../../ui/filtrosPesquisa/MultiSelectRelatorio";
import SelectTriEstado from "../../ui/filtrosPesquisa/SelectTriEstado";
import "../../ui/filtrosPesquisa/FiltrosPesquisa.scss";

moment.locale("pt");

const FILTROS_VAZIOS = {
  // Escolha múltipla
  criadoPor: [],
  medicoResponsavel: [],
  tipoCobertura: [],
  instituicaoCobertura: [],
  estabelecimento: [],
  statusPagamento: [],
  estadoOrcamento: [],
  // Cliente
  cliente: null,
  nomeCliente: "",
  numero: "",
  // Valores
  valorMin: "",
  valorMax: "",
  // Três estados
  transformado: "",
  terminado: "",
  substituido: "",
  rascunho: "",
  comDesconto: "",
  entregueNaClinica: "",
  enviadoInstituicao: "",
  comCredencial: "",
};

const CORES_PAGAMENTO = {
  Pago: "success",
  Parcial: "warning",
  Pendente: "error",
  "Sem conta": "default",
};

const CORES_COBERTURA = {
  PARTICULAR: "secondary",
  SEGURADO: "primary",
  MISTO: "warning",
};

const CartaoResumo = ({ titulo, valor, nota, cor = "primary.main", icone }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent sx={{ py: 1.5 }}>
      <Box
        sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
      >
        <Typography variant="body2" color="textSecondary">
          {titulo}
        </Typography>
        {icone}
      </Box>
      <Typography variant="h5" sx={{ color: cor, fontWeight: "bold" }}>
        {valor}
      </Typography>
      {nota && (
        <Typography variant="caption" color="textSecondary">
          {nota}
        </Typography>
      )}
    </CardContent>
  </Card>
);

/**
 * Relatório de pesquisa de orçamentos.
 *
 * ─── OS TRÊS NÚMEROS DO TOPO ────────────────────────────────────────────────
 *
 *   Valor total   — a soma do que foi orçamentado no período.
 *   Recebido      — o que já entrou, somando as parcelas pagas.
 *   Por receber   — o que falta receber DOS ORÇAMENTOS COM CONTA ABERTA.
 *
 * O terceiro não é «total menos recebido». Um orçamento sem conta aberta não é
 * uma dívida: ninguém o aprovou ainda, e talvez nunca seja feito. Esse valor
 * aparece à parte, em «não aprovado» — somá-lo à cobrança inflacionava o que a
 * clínica acha que tem a receber.
 */
function RelatorioPesquisaOrcamentos({
  dataFiltro,
  dadosClinica,
  auth,
  acao,
  atualizacoes,
}) {
  const { startLoading, stopLoading } = useLoading();
  const { sendRequest } = useHttpClient();
  const [dados, setDados] = useState(null);
  const [error, setError] = useState(null);
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [mostrarAgrupamentos, setMostrarAgrupamentos] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [procuraLocal, setProcuraLocal] = useState("");
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(25);

  const clinicaId = dadosClinica._id;
  const mudar = (campo, valor) =>
    setFiltros((prev) => ({ ...prev, [campo]: valor }));

  const moeda = (v) =>
    new Intl.NumberFormat("pt-CV", {
      style: "currency",
      currency: "CVE",
      maximumFractionDigits: 0,
    }).format(v || 0);

  /* Só a lista de clientes vem de fora. As restantes listas de filtros vêm do
     próprio relatório, tiradas dos dados do período — assim incluem quem já
     saiu da clínica, que a lista de utilizadores ativos não dava. */
  useEffect(() => {
    const carregarClientes = async () => {
      try {
        const res = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/clientes/datagrid/${clinicaId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        setClientes(res.clientes || []);
      } catch (err) {
        console.error("Erro ao carregar clientes:", err);
      }
    };
    carregarClientes();
  }, [clinicaId, sendRequest, auth.token]);

  const gerarRelatorio = useCallback(async () => {
    setError(null);
    startLoading();
    try {
      /* JSON e não FormData: os filtros de escolha múltipla são listas, e numa
         FormData uma lista vira campos repetidos que chegam ao servidor como
         texto — com um só valor escolhido já nem era uma lista. */
      const body = {
        ...filtros,
        cliente: filtros.cliente?.id || undefined,
        dataInicio: dataFiltro.dataInicial.format("YYYY-MM-DD"),
        dataFim: dataFiltro.dataFinal.format("YYYY-MM-DD"),
      };

      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos/relatorio-pesquisa/${clinicaId}`,
        "POST",
        JSON.stringify(body),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      setDados(resposta);
      setPagina(0);
    } catch (err) {
      setError(err.message || "Erro ao gerar o relatório");
      toast.error("Erro ao gerar o relatório");
    } finally {
      stopLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicaId, dataFiltro, filtros, auth.token]);

  /* ── Exportação ───────────────────────────────────────────────────────────
   * Exporta o que está no ecrã, incluindo a procura escrita na caixa: o que se
   * vê é o que sai. A versão anterior ia pedir tudo outra vez ao servidor e
   * dava um ficheiro que não correspondia à tabela. */
  const exportarRelatorio = useCallback(() => {
    if (!dados) {
      toast.error("É necessário gerar o relatório primeiro.");
      return;
    }

    const linhas = filtrarLocalmente(dados.orcamentos, procuraLocal);
    const wb = XLSX.utils.book_new();

    const cabecalho = [
      "Nº",
      "Cliente",
      "Contacto",
      "NIF",
      "Data",
      "Cobertura",
      "Instituição",
      "Modalidade",
      "Unidade",
      "Valor bruto",
      "Desconto",
      "Valor total",
      "Cobertura instituição",
      "Recebido",
      "Por receber",
      "Pagamento",
      "Parcelas pagas",
      "Último pagamento",
      "Aprovado",
      "Concluído",
      "Médico",
      "Criado por",
      "Aprovado por",
      "Nº credencial",
      "Enviado à instituição",
      "Entregue na clínica",
      "Avaliação",
      "Procedimentos",
      "Status",
    ];

    const ws = XLSX.utils.aoa_to_sheet([
      ["Relatório de Pesquisa de Orçamentos"],
      [
        `Período: ${dataFiltro.dataInicial.format(
          "DD/MM/YYYY",
        )} a ${dataFiltro.dataFinal.format("DD/MM/YYYY")}`,
      ],
      [`Gerado a ${moment().format("DD/MM/YYYY HH:mm")}`],
      [],
      cabecalho,
      ...linhas.map((o) => [
        o.numero || "-",
        o.cliente?.nome || "-",
        o.cliente?.contacto || "-",
        o.cliente?.nif || "-",
        moment(o.data).format("DD/MM/YYYY"),
        o.tipoCobertura,
        o.instituicao || "-",
        o.modalidade || "-",
        o.unidade || "-",
        o.valorBruto,
        o.desconto,
        o.valorTotal,
        o.valorCobertura,
        o.valorPago,
        o.valorPendente,
        o.statusPagamento,
        o.nrParcelasPagas,
        o.ultimoPagamento
          ? moment(o.ultimoPagamento).format("DD/MM/YYYY")
          : "-",
        o.transformado ? "Sim" : "Não",
        o.terminado ? "Sim" : "Não",
        o.medicoResponsavel || "-",
        o.criadoPor || "-",
        o.aprovadoPor || "-",
        o.nrCredencial || "-",
        o.enviadoInstituicao ? "Sim" : "Não",
        o.entregueNaClinica ? "Sim" : "Não",
        o.avaliacao || "-",
        o.nrProcedimentos,
        o.status || "-",
      ]),
    ]);

    const estiloCabecalho = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "366092" } },
      alignment: { horizontal: "center" },
    };
    cabecalho.forEach((_, i) => {
      const ref = XLSX.utils.encode_cell({ r: 4, c: i });
      if (ws[ref]) ws[ref].s = estiloCabecalho;
    });
    ws["!cols"] = cabecalho.map((c) => ({ wch: Math.max(12, c.length + 3) }));
    XLSX.utils.book_append_sheet(wb, ws, "Orçamentos");

    const r = dados.resumo;
    const grupo = (titulo, grupos) => [
      [],
      [titulo, "Nº", "Valor total", "Recebido", "Por receber", "Desconto"],
      ...grupos.map((g) => [
        g.nome,
        g.total,
        g.valorTotal,
        g.recebido,
        g.porReceber,
        g.desconto,
      ]),
    ];

    const resumo = [
      ["RESUMO"],
      [],
      ["Orçamentos", r.totalOrcamentos],
      ["Valor bruto (antes de desconto)", r.valorBruto],
      ["Desconto concedido", r.totalDesconto],
      ["Valor total", r.valorTotal],
      ["Coberto pela instituição", r.totalCobertura],
      [],
      ["Recebido", r.totalRecebido],
      ["Por receber (com conta aberta)", r.totalPorReceber],
      ["Ainda não aprovado (sem conta)", r.valorSemConta],
      [],
      ["Com conta aberta", r.nrComConta],
      ["Sem conta", r.nrSemConta],
      ["Totalmente pagos", r.totalmentePagos],
      ["Parcialmente pagos", r.parcialmentePagos],
      ["Por pagar", r.porPagar],
      [],
      ["Aprovados", r.aprovados],
      ["Concluídos", r.terminados],
      ["Substituídos", r.substituidos],
      ["Rascunhos", r.rascunhos],
      ["Taxa de aprovação (%)", r.taxaAprovacao],
      ["Taxa de cobrança (%)", r.taxaCobranca],
      ["Procedimentos", r.nrProcedimentos],
      ...grupo("POR COBERTURA", r.porCobertura),
      ...grupo("POR INSTITUIÇÃO", r.porInstituicao),
      ...grupo("POR ESTADO DE PAGAMENTO", r.porStatusPagamento),
      ...grupo("POR MÉDICO", r.porMedico),
      ...grupo("POR CRIADOR", r.porCriador),
      ...grupo("POR UNIDADE", r.porUnidade),
      ...grupo("POR MÊS", r.porMes),
    ];

    const wsResumo = XLSX.utils.aoa_to_sheet(resumo);
    wsResumo["A1"].s = { font: { bold: true, sz: 14 } };
    wsResumo["!cols"] = [
      { wch: 36 },
      { wch: 10 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 14 },
    ];
    XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo");

    XLSX.writeFile(
      wb,
      `relatorio_orcamentos_${moment().format("YYYY-MM-DD_HHmm")}.xlsx`,
    );
    toast.success("Ficheiro exportado.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados, procuraLocal, dataFiltro]);

  useEffect(() => {
    if (acao === "gerar") gerarRelatorio();
    else if (acao === "exportar") exportarRelatorio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acao, atualizacoes]);

  const opcoes = dados?.opcoes || {};
  const linhasVisiveis = useMemo(
    () => filtrarLocalmente(dados?.orcamentos || [], procuraLocal),
    [dados, procuraLocal],
  );
  const pagina0 = pagina * porPagina;
  const linhasPagina = linhasVisiveis.slice(pagina0, pagina0 + porPagina);

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Relatório de Pesquisa de Orçamentos
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Mostrar/ocultar filtros">
              <IconButton onClick={() => setMostrarFiltros((v) => !v)}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            {dados && (
              <Button
                startIcon={<FileDownloadIcon />}
                onClick={exportarRelatorio}
                variant="contained"
              >
                Exportar Excel
              </Button>
            )}
          </Box>
        </Box>

        {/* ── Filtros ───────────────────────────────────────────────────── */}
        <Collapse in={mostrarFiltros}>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2 }}>
              Filtros
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <MultiSelectRelatorio
                label="Criado por"
                value={filtros.criadoPor}
                onChange={(v) => mudar("criadoPor", v)}
                opcoes={opcoes.criadores || []}
                largura={260}
              />
              <MultiSelectRelatorio
                label="Médico responsável"
                value={filtros.medicoResponsavel}
                onChange={(v) => mudar("medicoResponsavel", v)}
                opcoes={opcoes.medicos || []}
                largura={260}
              />
              <MultiSelectRelatorio
                label="Cobertura"
                value={filtros.tipoCobertura}
                onChange={(v) => mudar("tipoCobertura", v)}
                opcoes={opcoes.coberturas || []}
                chaveDe={(o) => o.valor}
                rotuloDe={(o) => o.valor}
                largura={200}
              />
              <MultiSelectRelatorio
                label="Instituição"
                value={filtros.instituicaoCobertura}
                onChange={(v) => mudar("instituicaoCobertura", v)}
                opcoes={opcoes.instituicoes || []}
                largura={220}
              />
              <MultiSelectRelatorio
                label="Unidade"
                value={filtros.estabelecimento}
                onChange={(v) => mudar("estabelecimento", v)}
                opcoes={opcoes.unidades || []}
                rotuloDe={(u) => `${u.codigo} — ${u.nome}`}
                largura={240}
              />
              <MultiSelectRelatorio
                label="Pagamento"
                value={filtros.statusPagamento}
                onChange={(v) => mudar("statusPagamento", v)}
                opcoes={(opcoes.estadosPagamento || []).map((e) => ({
                  valor: e,
                }))}
                chaveDe={(o) => o.valor}
                rotuloDe={(o) => o.valor}
                totalDe={() => undefined}
                largura={200}
              />
              <MultiSelectRelatorio
                label="Status"
                value={filtros.estadoOrcamento}
                onChange={(v) => mudar("estadoOrcamento", v)}
                opcoes={opcoes.status || []}
                chaveDe={(o) => o.valor}
                rotuloDe={(o) => o.valor}
                largura={200}
              />

              <TextField
                size="small"
                label="Nome do cliente"
                placeholder="parte do nome"
                value={filtros.nomeCliente}
                onChange={(e) => mudar("nomeCliente", e.target.value)}
                sx={{ width: 220 }}
                disabled={!!filtros.cliente}
                helperText={filtros.cliente ? "a usar o cliente escolhido" : " "}
              />

              <ClientesAutocomplete
                className="filtroPesquisa__cliente"
                allClientes={clientes.map((c) => ({
                  label: c.nome,
                  id: c._id,
                }))}
                cliente={filtros.cliente}
                setCliente={(c) => mudar("cliente", c)}
              />

              <TextField
                size="small"
                label="Nº do orçamento"
                value={filtros.numero}
                onChange={(e) => mudar("numero", e.target.value)}
                sx={{ width: 160 }}
              />
              <TextField
                size="small"
                label="Valor mínimo"
                type="number"
                value={filtros.valorMin}
                onChange={(e) => mudar("valorMin", e.target.value)}
                sx={{ width: 140 }}
              />
              <TextField
                size="small"
                label="Valor máximo"
                type="number"
                value={filtros.valorMax}
                onChange={(e) => mudar("valorMax", e.target.value)}
                sx={{ width: 140 }}
              />

              <SelectTriEstado
                label="Aprovado"
                value={filtros.transformado}
                onChange={(v) => mudar("transformado", v)}
              />
              <SelectTriEstado
                label="Concluído"
                value={filtros.terminado}
                onChange={(v) => mudar("terminado", v)}
              />
              <SelectTriEstado
                label="Substituído"
                value={filtros.substituido}
                onChange={(v) => mudar("substituido", v)}
              />
              <SelectTriEstado
                label="Rascunho"
                value={filtros.rascunho}
                onChange={(v) => mudar("rascunho", v)}
              />
              <SelectTriEstado
                label="Com desconto"
                value={filtros.comDesconto}
                onChange={(v) => mudar("comDesconto", v)}
                largura={160}
              />
              <SelectTriEstado
                label="Com credencial"
                value={filtros.comCredencial}
                onChange={(v) => mudar("comCredencial", v)}
                largura={170}
              />
              <SelectTriEstado
                label="Enviado à instituição"
                value={filtros.enviadoInstituicao}
                onChange={(v) => mudar("enviadoInstituicao", v)}
                largura={200}
              />
              <SelectTriEstado
                label="Entregue na clínica"
                value={filtros.entregueNaClinica}
                onChange={(v) => mudar("entregueNaClinica", v)}
                largura={190}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              <Button variant="contained" onClick={gerarRelatorio}>
                Aplicar filtros
              </Button>
              <Button
                onClick={() => {
                  setFiltros(FILTROS_VAZIOS);
                  setProcuraLocal("");
                }}
              >
                Limpar
              </Button>
            </Box>
          </Paper>
        </Collapse>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {dados?.truncado && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            São {dados.total} orçamentos e a lista mostra os primeiros{" "}
            {dados.maxLinhas}. <strong>Os totais contam todos.</strong> Para ver
            os restantes, aperte o período ou use mais filtros.
          </Alert>
        )}

        {/* ── Os números ────────────────────────────────────────────────── */}
        {dados && (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <CartaoResumo
                  titulo="Orçamentos"
                  valor={dados.resumo.totalOrcamentos}
                  nota={`${dados.resumo.aprovados} aprovados · ${dados.resumo.taxaAprovacao}%`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CartaoResumo
                  titulo="Valor total"
                  valor={moeda(dados.resumo.valorTotal)}
                  nota={`desconto concedido: ${moeda(
                    dados.resumo.totalDesconto,
                  )}`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CartaoResumo
                  titulo="Recebido"
                  valor={moeda(dados.resumo.totalRecebido)}
                  nota={`taxa de cobrança: ${dados.resumo.taxaCobranca}%`}
                  cor="success.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CartaoResumo
                  titulo="Por receber"
                  valor={moeda(dados.resumo.totalPorReceber)}
                  nota={`${dados.resumo.nrComConta} com conta aberta`}
                  cor="error.main"
                  icone={
                    <Tooltip
                      title={`Só conta os orçamentos com conta aberta. Mais ${moeda(
                        dados.resumo.valorSemConta,
                      )} em ${dados.resumo.nrSemConta} orçamento(s) ainda sem conta — esses não são dívida, ninguém os aprovou.`}
                    >
                      <InfoIcon fontSize="small" color="action" />
                    </Tooltip>
                  }
                />
              </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="subtitle1">
                  Por cobertura, instituição, pagamento, médico, criador,
                  unidade e mês
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setMostrarAgrupamentos((v) => !v)}
                >
                  {mostrarAgrupamentos ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </IconButton>
              </Box>

              <Collapse in={mostrarAgrupamentos}>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  {[
                    ["Cobertura", dados.resumo.porCobertura],
                    ["Instituição", dados.resumo.porInstituicao],
                    ["Estado de pagamento", dados.resumo.porStatusPagamento],
                    ["Médico responsável", dados.resumo.porMedico],
                    ["Criado por", dados.resumo.porCriador],
                    ["Unidade", dados.resumo.porUnidade],
                    ["Mês", dados.resumo.porMes],
                  ].map(([titulo, grupos]) => (
                    <Grid item xs={12} md={6} key={titulo}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        {titulo}
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                      {grupos.length === 0 && (
                        <Typography variant="body2" color="textSecondary">
                          Sem dados
                        </Typography>
                      )}
                      {grupos.map((g) => (
                        <Box
                          key={g.nome}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 2,
                            py: 0.25,
                          }}
                        >
                          <Typography variant="body2" noWrap>
                            {g.nome} <em>({g.total})</em>
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "nowrap",
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {moeda(g.valorTotal)} · rec. {moeda(g.recebido)} ·
                            falta {moeda(g.porReceber)}
                          </Typography>
                        </Box>
                      ))}
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
            </Paper>
          </>
        )}

        {/* ── Procura dentro do resultado ───────────────────────────────── */}
        {dados && (
          <TextField
            fullWidth
            size="small"
            placeholder="Procurar no resultado: cliente, nº, médico, criador, credencial…"
            value={procuraLocal}
            onChange={(e) => {
              setProcuraLocal(e.target.value);
              setPagina(0);
            }}
            sx={{ mb: 2 }}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
          />
        )}

        {!dados && !error && (
          <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
            Escolha o período e aperte «Gerar Relatório».
          </Typography>
        )}

        {/* ── A lista ───────────────────────────────────────────────────── */}
        {dados && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {linhasVisiveis.length} orçamento(s)
              {procuraLocal ? ` de ${dados.orcamentos.length} na lista` : ""}
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Nº</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="center">Cobertura</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Recebido</TableCell>
                    <TableCell align="right">Por receber</TableCell>
                    <TableCell align="center">Pagamento</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell>Médico</TableCell>
                    <TableCell>Criado por</TableCell>
                    <TableCell>Unidade</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linhasPagina.map((o) => (
                    <TableRow key={o._id} hover>
                      <TableCell>{o.numero || "-"}</TableCell>
                      <TableCell>
                        {o.cliente ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {/* Abre a ficha já na aba do orçamento. */}
                            <MuiLink
                              component={Link}
                              to={o.cliente.link}
                              color="inherit"
                              underline="hover"
                            >
                              {o.cliente.nome}
                            </MuiLink>
                            <Tooltip title="Abrir o orçamento do cliente">
                              <IconButton
                                size="small"
                                component={Link}
                                to={o.cliente.link}
                              >
                                <OpenInNewIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {moment(o.data).format("DD/MM/YYYY")}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            o.instituicao
                              ? `${o.instituicao}${
                                  o.modalidade ? ` · ${o.modalidade}` : ""
                                }`
                              : "Particular"
                          }
                        >
                          <Chip
                            size="small"
                            label={
                              o.tipoCobertura === "PARTICULAR"
                                ? "Particular"
                                : o.instituicao || o.tipoCobertura
                            }
                            color={
                              CORES_COBERTURA[o.tipoCobertura] || "default"
                            }
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {moeda(o.valorTotal)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {moeda(o.valorPago)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {o.temConta ? moeda(o.valorPendente) : "—"}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          variant="outlined"
                          label={o.statusPagamento}
                          color={
                            CORES_PAGAMENTO[o.statusPagamento] || "default"
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            flexWrap: "wrap",
                            justifyContent: "center",
                          }}
                        >
                          {o.rascunho && (
                            <Chip size="small" label="Rascunho" />
                          )}
                          {o.substituido && (
                            <Chip
                              size="small"
                              label="Substituído"
                              color="default"
                            />
                          )}
                          {o.transformado && (
                            <Chip
                              size="small"
                              label="Aprovado"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                          {o.terminado && (
                            <Chip
                              size="small"
                              label="Concluído"
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {o.desconto > 0 && (
                            <Tooltip title={`Desconto: ${moeda(o.desconto)}`}>
                              <Chip
                                size="small"
                                label="Desc."
                                color="warning"
                                variant="outlined"
                              />
                            </Tooltip>
                          )}
                          {o.nrCredencial && (
                            <Tooltip title={`Credencial ${o.nrCredencial}`}>
                              <Chip size="small" label="Cred." />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{o.medicoResponsavel || "-"}</TableCell>
                      <TableCell>{o.criadoPor || "-"}</TableCell>
                      <TableCell>{o.unidade || "-"}</TableCell>
                    </TableRow>
                  ))}

                  {linhasVisiveis.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ py: 3 }}
                        >
                          Nenhum orçamento com estes filtros.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={linhasVisiveis.length}
              page={pagina}
              onPageChange={(e, nova) => setPagina(nova)}
              rowsPerPage={porPagina}
              onRowsPerPageChange={(e) => {
                setPorPagina(parseInt(e.target.value, 10));
                setPagina(0);
              }}
              labelRowsPerPage="Registos por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
            />
          </>
        )}
      </Paper>
    </Box>
  );
}

/** Procura em tudo o que a linha mostra. */
function filtrarLocalmente(linhas, termo) {
  const t = String(termo || "")
    .trim()
    .toLowerCase();
  if (!t) return linhas;

  return linhas.filter((o) =>
    [
      o.numero,
      o.cliente?.nome,
      o.cliente?.contacto,
      o.cliente?.nif,
      o.medicoResponsavel,
      o.criadoPor,
      o.aprovadoPor,
      o.instituicao,
      o.unidade,
      o.status,
      o.nrCredencial,
      o.avaliacao,
    ]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(t)),
  );
}

export default RelatorioPesquisaOrcamentos;
